import Reveal from "@/components/Reveal";
import Downloads from "@/components/Downloads";
import { Target, ShieldCheck, PieChart, BadgeCheck } from "lucide-react";

const goals = [
  { value: "100+", label: "Women & Children Supported", note: "through New Beginnings™ resources, essential needs, and The Next Chapter initiative" },
  { value: "2026–2027", label: "UNSPOKEN™ Launch", note: "building our first year of men's mental health and wellness experiences, conversations, and community partnerships" },
  { value: "GROWING", label: "Community Partner Network", note: "local businesses and organizations helping us expand resources, experiences, and support throughout our community" },
  { value: "100%", label: "Mission-Focused", note: "every contribution supports the charitable mission, programs, resources, and work of Tandem T Foundation." },
];

const allocation = [
  { label: "Programs & Direct Relief", pct: 85 },
  { label: "Operations", pct: 10 },
  { label: "Fundraising", pct: 5 },
];

export default function ImpactTransparency({ showTransparency = true }) {
  return (
    <>
      {/* IMPACT / GOALS */}
      <section className="py-24 md:py-32 border-b border-white/10" data-testid="foundation-impact">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="flex items-center gap-3 text-white/55">
              <Target size={18} /> <p className="overline">Our Commitment</p>
            </div>
            <h2 className="font-serif font-light text-white text-4xl md:text-6xl mt-4 leading-[1.05] tracking-tight max-w-4xl">
              A new foundation, with <em className="italic">measurable intentions.</em>
            </h2>
            <p className="text-white/70 font-light leading-relaxed mt-8 max-w-3xl text-lg">
              We are just beginning — and we believe in accountability from day one. These are the goals we
              are working toward this year, and we will report our real progress against them as we grow.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {goals.map((g, i) => (
              <Reveal key={g.label} delay={i * 0.06} className="bg-black">
                <div className="p-8 min-h-[200px] flex flex-col justify-between">
                  <p className="font-serif text-5xl md:text-6xl font-light text-white leading-none">{g.value}</p>
                  <div className="mt-6">
                    <p className="text-white font-light">{g.label}</p>
                    <p className="text-white/45 text-xs mt-1 leading-relaxed">{g.note}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TRANSPARENCY */}
      {showTransparency && (
      <section className="py-24 md:py-32 border-b border-white/10" data-testid="foundation-transparency">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <Reveal>
              <div className="flex items-center gap-3 text-white/55">
                <ShieldCheck size={18} /> <p className="overline">Transparency & Trust</p>
              </div>
              <h2 className="font-serif font-light text-white text-4xl md:text-5xl mt-4 leading-tight tracking-tight">
                Where your <em className="italic">gift goes.</em>
              </h2>
              <p className="text-white/70 font-light leading-relaxed mt-8 max-w-xl">
                Tandem T Foundation is a federally recognized 501(c)(3) nonprofit organization. Contributions
                are tax-deductible to the extent permitted by law. Your support helps us strengthen families,
                restore dignity, and create meaningful opportunities for individuals and communities across Florida.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-10 space-y-5 max-w-lg">
                {allocation.map((a) => (
                  <div key={a.label}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white/80 font-light">{a.label}</span>
                      <span className="text-white overline">{a.pct}%</span>
                    </div>
                    <div className="mt-2 h-1.5 bg-white/10">
                      <div className="h-full bg-white" style={{ width: `${a.pct}%` }} />
                    </div>
                  </div>
                ))}
                <p className="text-xs text-white/40 font-light pt-2">Target allocation; actuals reported as the Foundation matures.</p>
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-6">
            <Reveal delay={0.12}>
              <div className="border border-white/15 p-8 bg-white/[0.02] h-full">
                <div className="flex items-center gap-3">
                  <BadgeCheck size={20} className="text-white" />
                  <p className="font-serif text-2xl font-light text-white">Accountability</p>
                </div>
                <ul className="mt-6 space-y-5 text-sm">
                  <li className="flex items-start gap-3">
                    <PieChart size={16} className="mt-0.5 shrink-0 text-white/70" />
                    <span className="text-white/75 font-light">Federally recognized 501(c)(3) nonprofit organization — contributions are tax-deductible to the extent permitted by law.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ShieldCheck size={16} className="mt-0.5 shrink-0 text-white/70" />
                    <span className="text-white/75 font-light">Independent governance, dedicated bank accounts, and separate financial records held solely for our charitable mission.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <BadgeCheck size={16} className="mt-0.5 shrink-0 text-white/70" />
                    <span className="text-white/75 font-light">Our IRS 501(c)(3) determination letter — including our EIN — is available to download below.</span>
                  </li>
                </ul>
                <div className="mt-8 flex flex-wrap gap-2">
                  <span className="overline px-3 py-2 border border-white/20 text-white/80">501(c)(3) Nonprofit</span>
                  <span className="overline px-3 py-2 border border-white/20 text-white/80">Tax-Deductible</span>
                  <span className="overline px-3 py-2 border border-white/20 text-white/80">Florida Nonprofit</span>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="md:col-span-12">
            <div className="mt-12 border-t border-white/10 pt-10">
              <p className="overline text-white/50">For Funders &amp; Grantmakers</p>
              <p className="text-white/65 font-light mt-3 max-w-2xl">
                Everything you need for due diligence, in one click.
              </p>
              <Downloads className="mt-6" />
            </div>
          </Reveal>
        </div>
      </section>
      )}
    </>
  );
}
